/**
 * SignatureEditor — Iter228
 *
 * Modal drawer that lets a Smith user manage their rich outbound signature.
 * Backend: /api/smith/outreach/signature (GET / PUT / DELETE).
 *
 * Features:
 *  • Rich HTML entry (contenteditable) with live iframe-sandboxed preview
 *  • One-click "Importar assinatura" from clipboard HTML (paste)
 *  • Insert image URL (logo / avatar) with visual preview
 *  • Insert contact link (mailto / tel / website)
 *  • Reset to corporate default with a single click
 *  • Save persists to Mongo — every future send auto-appends this block
 */
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, Save, Trash2, Image as ImageIcon, Link as LinkIcon,
         RefreshCcw, Loader2, ClipboardCheck, Bold, Italic, Sparkles } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SignatureEditor({ onClose, onSaved }) {
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [lang, setLang] = useState("en");          // Iter230 · edit view lang
  const [htmlEn, setHtmlEn] = useState("");
  const [htmlPt, setHtmlPt] = useState("");
  const [text, setText] = useState("");
  const [name, setName] = useState("");
  const [replyTo, setReplyTo] = useState("");
  const [exists, setExists] = useState(false);
  const [presets, setPresets] = useState([]);
  const [showPresets, setShowPresets] = useState(false);
  const editorRef = useRef(null);
  const previewRef = useRef(null);

  // Live html for the currently-selected language
  const html = lang === "pt" ? htmlPt : htmlEn;
  const setHtml = (v) => (lang === "pt" ? setHtmlPt(v) : setHtmlEn(v));

  useEffect(() => { load(); loadPresets(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/smith/outreach/signature`);
      // Iter230 · Read per-lang variants if present, else fill both with `html`
      const baseHtml = data.html || "";
      setHtmlEn(data.html_en || baseHtml);
      setHtmlPt(data.html_pt || baseHtml);
      setText(data.text || "");
      setName(data.name || "");
      setReplyTo(data.reply_to || "");
      setExists(!!data.exists);
      setShowPresets(!data.exists);
      setTimeout(() => {
        if (editorRef.current) editorRef.current.innerHTML =
          (data.html_en || baseHtml);
      }, 30);
    } catch (e) {
      toast.error("Falha ao carregar assinatura");
    } finally { setLoading(false); }
  };

  // Re-sync editor DOM when the lang tab changes so the operator edits
  // the correct variant.
  useEffect(() => {
    if (editorRef.current) editorRef.current.innerHTML = html || "";
  }, [lang]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadPresets = async () => {
    try {
      const { data } = await axios.get(`${API}/smith/outreach/signature/presets`);
      setPresets(data.presets || []);
    } catch { /* silent */ }
  };

  const applyPreset = (preset) => {
    // Iter230 · Apply BOTH language variants at once when the preset
    // supports it — operator can still tweak either after.
    if (preset.html_en) setHtmlEn(preset.html_en);
    if (preset.html_pt) setHtmlPt(preset.html_pt);
    if (!preset.html_en && !preset.html_pt) {
      setHtmlEn(preset.html); setHtmlPt(preset.html);
    }
    const shown = lang === "pt" ? (preset.html_pt || preset.html) : (preset.html_en || preset.html);
    if (editorRef.current) editorRef.current.innerHTML = shown;
    setShowPresets(false);
    toast.success(`Preset "${preset.name}" aplicado — PT e EN carregados`);
  };

  // Update the iframe preview whenever html changes.
  useEffect(() => {
    if (!previewRef.current) return;
    const doc = previewRef.current.contentDocument;
    if (doc) {
      doc.open();
      doc.write(`<!doctype html><html><body style="margin:0;padding:16px;font-family:-apple-system,BlinkMacSystemFont,Segoe UI,sans-serif;background:#ffffff;color:#0f172a;font-size:13.5px;line-height:1.5;">
        <div style="padding:12px 16px;background:#f8fafc;border-radius:6px;color:#64748b;font-size:12px;margin-bottom:16px;">Prévia da assinatura como será renderizada no e-mail</div>
        <div>Olá, boa tarde!</div><br/>
        <div>Segue conforme conversamos… (corpo do e-mail).</div>
        <hr style="border:none;border-top:1px solid #E5E7EB;margin:20px 0 12px 0;"/>
        ${html || "<div style=\"color:#9ca3af;font-style:italic;\">(assinatura vazia)</div>"}
      </body></html>`);
      doc.close();
    }
  }, [html]);

  const handleEditorInput = (e) => setHtml(e.currentTarget.innerHTML);

  const insertImage = () => {
    const url = window.prompt("Cole a URL da imagem (logo, banner, avatar):", "https://");
    if (!url || !/^https?:\/\//i.test(url)) return;
    const alt = window.prompt("Texto alternativo (alt):", "Smith Protective") || "";
    document.execCommand("insertHTML", false,
      `<img src="${url}" alt="${alt}" style="max-height:60px;vertical-align:middle;"/>`);
    setHtml(editorRef.current.innerHTML);
  };

  const insertLink = () => {
    const label = window.prompt("Texto do link:", "www.smith1903.com");
    if (!label) return;
    let url = window.prompt("URL destino:", "https://www.smith1903.com");
    if (!url) return;
    if (!/^(https?:|mailto:|tel:)/i.test(url)) url = `https://${url}`;
    document.execCommand("insertHTML", false,
      `<a href="${url}" style="color:#DC2626;text-decoration:none;">${label}</a>`);
    setHtml(editorRef.current.innerHTML);
  };

  const format = (cmd) => { document.execCommand(cmd, false, null); setHtml(editorRef.current.innerHTML); };

  const pasteRich = async () => {
    try {
      const clipItems = await navigator.clipboard.read();
      for (const item of clipItems) {
        if (item.types.includes("text/html")) {
          const blob = await item.getType("text/html");
          const raw = await blob.text();
          // Strip <html>/<body> wrapper and inline styles-only images so
          // the paste is safe to preview.
          const cleaned = raw
            .replace(/<\/?(html|head|body|meta|title)[^>]*>/gi, "")
            .replace(/<script[\s\S]*?<\/script>/gi, "");
          if (editorRef.current) editorRef.current.innerHTML = cleaned;
          setHtml(cleaned);
          toast.success("Assinatura importada da área de transferência");
          return;
        }
      }
      toast.warning("Nenhum HTML na área de transferência — copie a assinatura direto do Gmail/Outlook");
    } catch (e) {
      toast.error("Permissão negada — cole manualmente com Ctrl+V no editor");
    }
  };

  const save = async () => {
    // Iter230 · Require at least one variant to be non-empty
    const combined = (htmlEn || "") + (htmlPt || "");
    if (!combined || combined.replace(/<[^>]+>/g, "").trim().length < 4) {
      toast.error("A assinatura precisa conter conteúdo mínimo");
      return;
    }
    setBusy(true);
    try {
      // Preserve back-compat: send `html` = current-lang variant + both variants.
      const primary = htmlEn || htmlPt;
      await axios.put(`${API}/smith/outreach/signature`, {
        html: primary,
        html_en: htmlEn || primary,
        html_pt: htmlPt || primary,
        text: text || primary.replace(/<[^>]+>/g, "").trim(),
        name, reply_to: replyTo, is_default: true,
      });
      toast.success("Assinatura PT/EN salva — a variante certa será usada em cada disparo");
      setExists(true);
      onSaved && onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao salvar assinatura");
    } finally { setBusy(false); }
  };

  const resetDefault = async () => {
    if (!window.confirm("Restaurar a assinatura corporativa padrão (Marcelo Borges)?")) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/smith/outreach/signature`);
      toast.success("Assinatura restaurada para o padrão corporativo");
      onSaved && onSaved();
      load();
    } catch (e) {
      toast.error("Falha ao restaurar");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-stretch justify-end"
         data-testid="signature-editor-modal"
         style={{ background: "rgba(2,6,23,0.6)" }} onClick={onClose}>
      <div className="w-full max-w-[1100px] h-full flex flex-col shadow-2xl"
           style={{ background: "linear-gradient(180deg,#0B1120 0%,#0F172A 100%)" }}
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-3 border-b border-white/10 shrink-0">
          <div>
            <h2 className="text-white text-[15px] font-black">Assinatura de E-mail</h2>
            <p className="text-[11px] text-slate-400">
              {exists ? "Assinatura personalizada ativa — aplicada em todos os disparos"
                      : "Usando bloco corporativo padrão — personalize sua assinatura abaixo"}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {/* Iter230 · PT/EN language toggle */}
            <div className="inline-flex rounded-lg border border-white/10 bg-white/5 p-0.5"
                 data-testid="signature-lang-toggle">
              <button type="button" onClick={() => setLang("en")}
                data-testid="signature-lang-en"
                className={`h-7 px-3 text-[10.5px] font-black rounded transition-colors ${
                  lang === "en" ? "bg-sky-500/25 text-sky-200" : "text-slate-400 hover:text-white"}`}>
                🇺🇸 EN
              </button>
              <button type="button" onClick={() => setLang("pt")}
                data-testid="signature-lang-pt"
                className={`h-7 px-3 text-[10.5px] font-black rounded transition-colors ${
                  lang === "pt" ? "bg-emerald-500/25 text-emerald-200" : "text-slate-400 hover:text-white"}`}>
                🇧🇷 PT
              </button>
            </div>
            <button onClick={onClose} data-testid="signature-editor-close-btn"
                    className="w-8 h-8 rounded hover:bg-white/10 text-slate-400 flex items-center justify-center">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex-1 flex items-center justify-center">
            <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
          </div>
        ) : (
          <div className="flex-1 grid grid-cols-1 lg:grid-cols-2 gap-4 p-6 overflow-y-auto">
            {/* ── EDITOR ── */}
            <div className="flex flex-col gap-3">
              <div className="grid grid-cols-2 gap-2">
                <label className="text-[10.5px] font-bold text-slate-400 uppercase tracking-widest">
                  Nome de exibição
                  <input value={name} onChange={(e) => setName(e.target.value)}
                    data-testid="signature-input-name"
                    className="mt-1 w-full h-8 px-2 rounded bg-white/5 border border-white/10 text-[12px] text-white"
                    placeholder="Marcelo Borges" />
                </label>
                <label className="text-[10.5px] font-bold text-slate-400 uppercase tracking-widest">
                  Reply-To (opcional)
                  <input value={replyTo} onChange={(e) => setReplyTo(e.target.value)}
                    data-testid="signature-input-reply-to"
                    className="mt-1 w-full h-8 px-2 rounded bg-white/5 border border-white/10 text-[12px] text-white"
                    placeholder="mborges@smithprotective.com" />
                </label>
              </div>

              {/* Toolbar */}
              <div className="flex items-center gap-1 flex-wrap px-2 py-2 rounded-lg border border-white/10 bg-white/5">
                <button type="button" onClick={() => setShowPresets((v) => !v)}
                  className={`h-7 px-2 rounded text-[11px] font-bold flex items-center gap-1 transition-colors ${
                    showPresets ? "bg-purple-500/25 text-purple-200"
                                : "hover:bg-white/10 text-purple-300"}`}
                  data-testid="signature-btn-presets" title="Escolher template pronto">
                  <Sparkles className="w-3 h-3" /> Templates
                </button>
                <div className="w-px h-5 bg-white/10 mx-1" />
                <button type="button" onClick={() => format("bold")}
                  className="h-7 px-2 rounded hover:bg-white/10 text-slate-300 text-[11px] font-bold flex items-center gap-1"
                  data-testid="signature-btn-bold" title="Negrito">
                  <Bold className="w-3 h-3" /> B
                </button>
                <button type="button" onClick={() => format("italic")}
                  className="h-7 px-2 rounded hover:bg-white/10 text-slate-300 text-[11px] italic font-bold flex items-center gap-1"
                  data-testid="signature-btn-italic" title="Itálico">
                  <Italic className="w-3 h-3" /> I
                </button>
                <div className="w-px h-5 bg-white/10 mx-1" />
                <button type="button" onClick={insertImage}
                  className="h-7 px-2 rounded hover:bg-white/10 text-sky-300 text-[11px] font-bold flex items-center gap-1"
                  data-testid="signature-btn-image">
                  <ImageIcon className="w-3 h-3" /> Imagem
                </button>
                <button type="button" onClick={insertLink}
                  className="h-7 px-2 rounded hover:bg-white/10 text-amber-300 text-[11px] font-bold flex items-center gap-1"
                  data-testid="signature-btn-link">
                  <LinkIcon className="w-3 h-3" /> Link
                </button>
                <div className="w-px h-5 bg-white/10 mx-1" />
                <button type="button" onClick={pasteRich}
                  className="h-7 px-2 rounded hover:bg-white/10 text-emerald-300 text-[11px] font-bold flex items-center gap-1"
                  data-testid="signature-btn-paste"
                  title="Importar do Gmail/Outlook (Ctrl+C na assinatura, depois clique aqui)">
                  <ClipboardCheck className="w-3 h-3" /> Importar
                </button>
                <div className="ml-auto text-[9.5px] text-slate-500">
                  Cole (Ctrl+V) direto do Gmail/Outlook
                </div>
              </div>

              <div ref={editorRef}
                contentEditable suppressContentEditableWarning
                onInput={handleEditorInput}
                data-testid="signature-editor-html"
                className="flex-1 min-h-[240px] rounded-lg border border-white/10 bg-white text-[13px] text-slate-900 p-4 overflow-y-auto"
                style={{ fontFamily: "-apple-system,BlinkMacSystemFont,Segoe UI,sans-serif" }} />

              {/* Iter229 · Preset picker */}
              {showPresets && presets.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2"
                     data-testid="signature-presets-picker">
                  {presets.map((p) => (
                    <button key={p.id} type="button" onClick={() => applyPreset(p)}
                      data-testid={`signature-preset-${p.id}`}
                      className="text-left rounded-lg border border-purple-500/40 bg-purple-500/10 hover:bg-purple-500/20 p-3 transition-colors">
                      <div className="text-[12px] font-black text-white">{p.name}</div>
                      <div className="text-[10px] text-slate-400 mt-1 leading-tight">{p.description}</div>
                      <div className="text-[9px] text-purple-300 mt-2 uppercase tracking-widest font-bold">
                        Aplicar template →
                      </div>
                    </button>
                  ))}
                </div>
              )}

              <textarea value={text} onChange={(e) => setText(e.target.value)}
                data-testid="signature-input-text"
                rows={3}
                className="rounded-lg border border-white/10 bg-white/5 p-3 text-[11.5px] text-slate-300 resize-none"
                placeholder="Versão texto-simples (fallback para clientes que não renderizam HTML — geramos automaticamente se deixar em branco)" />
            </div>

            {/* ── PREVIEW ── */}
            <div className="flex flex-col">
              <div className="text-[10.5px] font-bold text-slate-400 uppercase tracking-widest mb-2">
                Prévia em cliente de e-mail
              </div>
              <iframe ref={previewRef} title="Signature Preview"
                data-testid="signature-editor-preview"
                sandbox=""
                className="flex-1 min-h-[520px] rounded-lg border border-white/10 bg-white" />
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center justify-between gap-2 px-6 py-3 border-t border-white/10 shrink-0"
             style={{ background: "rgba(11,17,32,0.9)" }}>
          <button type="button" onClick={resetDefault} disabled={busy || loading}
            data-testid="signature-editor-reset-btn"
            className="h-9 px-3 rounded-lg text-[12px] font-bold text-red-300 hover:bg-red-500/15 border border-red-500/40 inline-flex items-center gap-1.5 disabled:opacity-40">
            <Trash2 className="w-3.5 h-3.5" /> Restaurar padrão corporativo
          </button>
          <div className="flex items-center gap-2">
            <button type="button" onClick={onClose}
              className="h-9 px-3 rounded-lg text-[12px] font-bold text-slate-300 hover:bg-white/10">
              Cancelar
            </button>
            <button type="button" onClick={save} disabled={busy || loading}
              data-testid="signature-editor-save-btn"
              className="h-9 px-4 rounded-lg text-[12px] font-black text-white inline-flex items-center gap-1.5 disabled:opacity-40"
              style={{ background: "linear-gradient(135deg,#10B981 0%,#059669 100%)" }}>
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
              Salvar Assinatura
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
